import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { translate } from '../Services/translation';
import CaretDown from './Icons/CaretDown';

import {
    submitSearchFacet,
    toggleVisibleDropdownMenu,
    updateFilterOption
} from '../Actions/FacetedSearch.action';

const filterInitialShownAmount = 4;

const FacetedSearchCompact = ({ toggle, previousCachedId }) => {
    const dispatch = useDispatch();
    const { visibleDropdownMenu, facetFilters: globalFacetFilters, productsViewCachedId } = useSelector((state) => state.facetedSearch);
    const [facetFilters, setFacetFilters] = useState(globalFacetFilters || []);
    const [expandedGroups, setExpandedGroups] = useState({});
    const toggleGroupExpansion = (groupId) => {
        setExpandedGroups(prevState => ({
            ...prevState,
            [groupId]: !prevState[groupId],
        }));
    };
    const [isInitialSetupDone, setIsInitialSetupDone] = useState(false); // Handle initial visibility state, with the first group open by default

    useEffect(() => {
        if (previousCachedId.current !== productsViewCachedId) {
            setFacetFilters(globalFacetFilters);
            previousCachedId.current = productsViewCachedId;
        }
    }, [facetFilters, productsViewCachedId]);

    const onFacetChange = useCallback(
        (filter, option) => {
            setFacetFilters((prevFacetFilters) => {
                return updateFilterOption(prevFacetFilters, filter, option);
            });
        },
        [setFacetFilters]
    );

    useEffect(() => {
        if (!isInitialSetupDone && facetFilters.length > 0) {
            // Open the first dropdown only on initial render
            if (!visibleDropdownMenu.includes(facetFilters[0].id)) {
                dispatch(toggleVisibleDropdownMenu(facetFilters[0]));
            }
            // Mark the initial setup as done
            setIsInitialSetupDone(true);
        }
    }, [facetFilters, visibleDropdownMenu, dispatch, isInitialSetupDone]);

    return (
        <>
            <ul className="faceted-search faceted-search--compact">
                {facetFilters &&
                    facetFilters.map(
                        (group, groupIndex, array) =>
                            group.options &&
                            group.options.length > 0 && (
                                <li
                                    className="faceted-search__group"
                                    key={`${group.label}-${groupIndex}`}
                                    data-label={group.friendlyLabelKey}>

                                    <button
                                        className={`faceted-search__group-header ${visibleDropdownMenu.includes(group.id)
                                            ? 'faceted-search__group-header--show-compact'
                                            : '' 
                                        }`}
                                        role="faceted-search-item-group"
                                        onClick={(event) =>
                                            dispatch(
                                                toggleVisibleDropdownMenu(group)
                                            )
                                        }
                                    >
                                        {getDisplayValue(group)}

                                        <CaretDown 
                                            isOpen={visibleDropdownMenu.includes(group.id)}
                                            width='20'
                                            height='20'
                                            strokeWidth='2' />
                                    </button>

                                    <ul className="faceted-search__sublist">
                                        {group.options.slice(0, filterInitialShownAmount).map((item, itemIndex) => (
                                            <li
                                                key={`${item.label}-${itemIndex}`}
                                                className="faceted-search__item"
                                                role="faceted-search-item"
                                            >
                                                <FacetedFilterCheckbox
                                                    item={item}
                                                    group={group}
                                                    onFacetChange={onFacetChange}
                                                />
                                            </li>
                                        ))}

                                        {/* Show remaining options if expanded */}
                                        {expandedGroups[group.id] && group.options.slice(filterInitialShownAmount).map((item, itemIndex) => (
                                            <li
                                                key={`${item.label}-${itemIndex + filterInitialShownAmount}`}
                                                className="faceted-search__item"
                                                role="faceted-search-item"
                                            >
                                                <FacetedFilterCheckbox
                                                    item={item}
                                                    group={group}
                                                    onFacetChange={onFacetChange}
                                                />
                                            </li>
                                        ))}

                                        {group.options.length > filterInitialShownAmount && (
                                            <li className="faceted-search__item">
                                                <button
                                                    onClick={() => toggleGroupExpansion(group.id)}
                                                    className="faceted-search__show-more-button"
                                                >
                                                    {expandedGroups[group.id] 
                                                        ? (
                                                            <>
                                                                <span>-</span>
                                                                <span>{translate('filter.showless')}</span>
                                                            </>
                                                        )
                                                        : (
                                                            <>
                                                                <span>+</span>
                                                                <span>{translate('filter.showmore')}</span>
                                                            </>
                                                        )
                                                    }
                                                </button>
                                            </li>
                                        )}
                                    </ul>
                                </li>
                        )
                    )}
            </ul>

            <div className='faceted-search__action'>
                <button
                    className="button"
                    onClick={(event) => {                        
                        dispatch(
                            submitSearchFacet(
                                facetFilters
                            )
                        );
                    }}>
                    {translate('general.select')}
                </button>
            </div>
        </>
    );
};

const FacetedFilterCheckbox = ({ item, group, onFacetChange }) => (
    <label className="faceted-filter">
        <input
            className="faceted-filter__input"
            type="checkbox"
            onChange={(event) => onFacetChange(group, item)}
            checked={group.selectedOptions.includes(item.id)}
        />
        <span className="faceted-filter__label" data-label={item.friendlyLabelKey}>
            
            {getDisplayValue(item)}

            {!isNaN(item.quantity) && item.quantity > 0 && (
                <span className="faceted-filter__quantity">
                    {' '}
                    ({item.quantity})
                </span>
            )}
        </span>
    </label>
)

const getDisplayValue = (item) => {
    if (item.friendlyLabel != "" && item.friendlyLabel != null) {
        return item.friendlyLabel;
    }
    return item.label;
}

export default FacetedSearchCompact;
